import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../services/supabaseClient';
import { ApiService } from '../services/api';
import { Hire, Vehicle, ExtractedHireData, AssignmentType } from '../types';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import Modal from '../components/Modal';
import HireComplianceModal from '../components/HireComplianceModal';
import HireAgreementExtractModal from '../components/HireAgreementExtractModal';
import {
  Users,
  Plus,
  Car,
  Loader2,
  AlertCircle,
  Pencil,
  Save,
  Lock,
  Search,
  FileText,
  Upload,
  X,
  FileCheck,
  FileSearch
} from 'lucide-react';

interface HireFormData {
  vehicle_id: string;
  hire_ref: string;
  driver_name: string;
  driver_email: string;
  driver_phone: string;
  start_date: string;
  end_date: string;
  actual_return_date: string;
  assignment_type: AssignmentType | '';
  agreement_has_liability_clause: boolean;
  hirer_address?: string | null;
}

const initialFormState: HireFormData = {
  vehicle_id: '',
  hire_ref: '',
  driver_name: '',
  driver_email: '',
  driver_phone: '',
  start_date: '',
  end_date: '',
  actual_return_date: '',
  assignment_type: '',
  agreement_has_liability_clause: false,
  hirer_address: null
};

const Hires: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { checkAccess, promptUpgrade } = useSubscription();
  const hasAudit = checkAccess('agreementAudit');
  
  const [hires, setHires] = useState<Hire[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filterVrm, setFilterVrm] = useState('All');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
  const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<HireFormData>(initialFormState);
  const [editingHire, setEditingHire] = useState<Hire | null>(null);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');

  // File Upload State
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingEditHireId, setPendingEditHireId] = useState<string | null>(null);

  const openAgreement = async (agreementRef?: string | null) => {
    if (!agreementRef) return;

    try {
      const url = await ApiService.resolveDocumentUrl(agreementRef);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editHireId = params.get('editHire');
    if (editHireId) {
      setPendingEditHireId(editHireId);
      params.delete('editHire');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
    }
  }, [location.search]);

  useEffect(() => {
    if (!pendingEditHireId) return;
    const existing = hires.find((h) => h.id === pendingEditHireId);
    if (existing) {
      handleOpenEdit(existing);
      setPendingEditHireId(null);
      return;
    }

    const loadHire = async () => {
      try {
        const hire = await ApiService.getHireById(pendingEditHireId);
        if (hire) handleOpenEdit(hire);
      } catch (err) {
        addToast('Unable to open hire record.', 'error');
      } finally {
        setPendingEditHireId(null);
      }
    };

    void loadHire();
  }, [pendingEditHireId, hires, addToast]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') !== '1') return;

    setFormData(initialFormState);
    setAgreementFile(null);
    setIsModalOpen(true);
    params.delete('new');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch data in parallel
      const [vehiclesData, hiresData] = await Promise.all([
        ApiService.getVehicles(),
        ApiService.getHires()
      ]);
      
      setVehicles(vehiclesData || []);
      setHires(hiresData || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      const msg = getErrorMessage(err);
      if (msg === 'User not authenticated') {
        navigate('/login');
        return;
      }
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setEditingHire(null);
    setFormData(initialFormState);
    setAgreementFile(null);
    setVehicleSearchTerm('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsModalOpen(true);
  };

  const handleExtractedData = async (extractedData: ExtractedHireData, file: File) => {
    // Helper: Convert ISO dates to datetime-local format (YYYY-MM-DDTHH:mm)
    const formatDateTimeLocal = (isoDate: string): string => {
      if (!isoDate) return '';
      // If date-only format (YYYY-MM-DD), append default time
      if (isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return isoDate + 'T09:00';
      }
      // If full ISO format (YYYY-MM-DDTHH:mm:ss.sssZ), extract datetime-local part
      if (isoDate.includes('T')) {
        return isoDate.substring(0, 16); // Extract YYYY-MM-DDTHH:mm
      }
      return isoDate;
    };

    // FIX 1: Find vehicle by VRM (normalized comparison)
    let matchedVehicle = vehicles.find(
      v => v.vrm.toUpperCase().replace(/\s/g, '') === extractedData.vrm.toUpperCase().replace(/\s/g, '')
    );

    // FIX 4: Auto-create vehicle if not found
    if (!matchedVehicle) {
      try {
        addToast(`Creating vehicle ${extractedData.vrm}...`, 'info');

        // Create vehicle and use returned data directly (avoids race condition)
        const createdVehicle = await ApiService.saveVehicle({
          vrm: extractedData.vrm,
          make: undefined,
          model: undefined,
          colour: undefined,
          status: 'Active',
          notes: 'Auto-created from hire agreement extraction'
        });

        if (createdVehicle) {
          matchedVehicle = createdVehicle;
          // Update local vehicles state for consistency
          setVehicles([...vehicles, createdVehicle]);
          addToast(`Vehicle ${extractedData.vrm} created successfully`, 'success');
        } else {
          addToast(`Vehicle created but could not be matched. Please select manually.`, 'warning');
        }
      } catch (error: any) {
        console.error('Failed to auto-create vehicle:', error);
        addToast(`Could not create vehicle ${extractedData.vrm}: ${error.message}`, 'error');
      }
    }

    // PHASE 2.3: Auto-update vehicle owner from extraction
    // Smart fallback: If no owner info but company name exists, use company as owner (fleet-owned model)
    const ownerName = extractedData.vehicle_owner_name || extractedData.company_name;
    const ownerEmail = extractedData.vehicle_owner_email || null;
    const ownerPhone = extractedData.vehicle_owner_phone || null;

    // Only proceed if we have a VRM, owner name, AND at least one contact method
    // This aligns with manual validation: owner name requires email OR phone
    const hasValidOwnerContact = ownerEmail || ownerPhone;

    if (extractedData.vrm && ownerName && hasValidOwnerContact && matchedVehicle) {
      try {
        // Check if vehicle already has owner info
        if (!matchedVehicle.owner_name) {
          // Auto-update vehicle with extracted owner details
          const updatedVehicle = await ApiService.saveVehicle({
            id: matchedVehicle.id,
            vrm: matchedVehicle.vrm,
            owner_name: ownerName,
            owner_email: ownerEmail || '',
            owner_phone: ownerPhone || ''
          });

          if (updatedVehicle) {
            // Update vehicles state immutably using functional setState
            setVehicles(prevVehicles =>
              prevVehicles.map(v => v.id === updatedVehicle.id ? updatedVehicle : v)
            );

            // Provide helpful feedback about which model was used
            const modelType = extractedData.vehicle_owner_name ? 'owner' : 'company';
            const message = modelType === 'owner'
              ? `Updated vehicle ${extractedData.vrm} with owner information`
              : `Updated vehicle ${extractedData.vrm} with company as owner (fleet-owned model)`;
            addToast(message, 'success');
          }
        } else if (matchedVehicle.owner_name !== ownerName) {
          // Owner mismatch - warn user
          addToast(
            `⚠️ Owner mismatch: Vehicle shows "${matchedVehicle.owner_name}" but agreement shows "${ownerName}"`,
            'warning'
          );
        }
      } catch (error: any) {
        console.error('Failed to update vehicle owner:', error);
        addToast(`Could not update owner information: ${error.message}`, 'error');
      }
    } else if (extractedData.vrm && ownerName && !hasValidOwnerContact && matchedVehicle && !matchedVehicle.owner_name) {
      // Owner name extracted but no contact info - inform user
      addToast(
        `Owner name "${ownerName}" found but no contact details. Please add manually if needed.`,
        'info'
      );
    }

    // FIX 3: Auto-generate hire_ref if missing (use hire start date year)
    const hireYear = extractedData.start_date ? new Date(extractedData.start_date).getFullYear() : new Date().getFullYear();
    const hireRef = extractedData.hire_ref ||
      `HIRE-${hireYear}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

    setFormData({
      vehicle_id: matchedVehicle?.id || '',
      hire_ref: hireRef,
      driver_name: extractedData.driver_name,
      driver_email: extractedData.driver_email || '',
      driver_phone: extractedData.driver_phone || '',
      start_date: formatDateTimeLocal(extractedData.start_date), // FIX 1: Date format conversion
      end_date: formatDateTimeLocal(extractedData.end_date || ''), // FIX 1: Date format conversion
      actual_return_date: '',
      assignment_type: '',
      agreement_has_liability_clause: extractedData.has_liability_clause,
      hirer_address: extractedData.vehicle_owner_address || null
    });

    // FIX 2: Set vehicleSearchTerm so VRM is visible even if vehicle doesn't exist
    setVehicleSearchTerm(extractedData.vrm);

    // Set agreement file
    setAgreementFile(file);

    // Close extract modal and open add hire modal
    setIsExtractModalOpen(false);
    setEditingHire(null);
    setIsModalOpen(true);

    // Show success toast
    addToast(
      matchedVehicle
        ? `Extracted data for ${extractedData.vrm} - Vehicle matched automatically`
        : `Extracted data for ${extractedData.vrm} - Please fill in vehicle details`,
      'success'
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError(null);
    setAgreementFile(null);
    setEditingHire(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toDateTimeLocal = (raw: string | null | undefined) => {
    if (!raw) return '';
    const d = new Date(raw);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleOpenEdit = (hire: Hire) => {
    setEditingHire(hire);
    setFormData({
      vehicle_id: hire.vehicle_id || '',
      hire_ref: hire.hire_ref || '',
      driver_name: hire.driver_name || '',
      driver_email: hire.driver_email || '',
      driver_phone: hire.driver_phone || '',
      start_date: toDateTimeLocal(hire.start_date),
      end_date: toDateTimeLocal(hire.end_date),
      actual_return_date: toDateTimeLocal(hire.actual_return_date),
      assignment_type: hire.assignment_type || '',
      agreement_has_liability_clause: Boolean(hire.agreement_has_liability_clause)
    });
    setAgreementFile(null);

    // Find the vehicle to populate the search term
    const matchedVehicle = vehicles.find(v => v.id === hire.vehicle_id);
    if (matchedVehicle) {
      setVehicleSearchTerm(matchedVehicle.vrm);
      console.log(`✏️ Editing hire - Vehicle: ${matchedVehicle.vrm} (ID: ${matchedVehicle.id})`);
    } else {
      setVehicleSearchTerm('');
      console.warn(`⚠️ Editing hire but vehicle not found for ID: ${hire.vehicle_id}`);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsModalOpen(true);
  };

  const handleAuditClick = () => {
    if (!hasAudit) {
      promptUpgrade();
      return;
    }
    setIsComplianceModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAgreementFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare payload
      const payload: Partial<Hire> = {
        id: editingHire?.id,
        vehicle_id: formData.vehicle_id || undefined,
        hire_ref: formData.hire_ref || null,
        driver_name: formData.driver_name,
        driver_email: formData.driver_email || null,
        driver_phone: formData.driver_phone || null,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        actual_return_date: formData.actual_return_date || null,
        assignment_type: formData.assignment_type || null,
        agreement_has_liability_clause: formData.agreement_has_liability_clause,
        hirer_address: formData.hirer_address || null
      };

      // Log payload for debugging
      console.log('💾 Saving hire with payload:', {
        ...payload,
        vehicle_id: payload.vehicle_id,
        vehicle_id_type: typeof payload.vehicle_id,
        has_agreement_file: !!agreementFile
      });

      // Call API with optional file
      await ApiService.saveHire(payload, agreementFile || undefined);
      
      addToast(editingHire ? "Hire updated successfully" : "Hire added successfully", 'success');
      
      // Refresh data
      const newHires = await ApiService.getHires();
      setHires(newHires || []);
      
      handleCloseModal();

      const params = new URLSearchParams(location.search);
      const returnTicket = params.get('returnTicket');
      const resumeTransfer = params.get('resumeTransfer');
      if (returnTicket) {
        const suffix = resumeTransfer ? '?transfer=1&hireUpdated=1' : '';
        navigate(`/tickets/${returnTicket}${suffix}`);
        return;
      }
    } catch (err: any) {
      const msg = getErrorMessage(err);
      if (msg === 'User not authenticated') {
        navigate('/login');
        return;
      }
      setError(`Failed to save: ${msg}`);
      addToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter Logic
  const filteredHires = hires.filter(hire => {
    // 1. VRM Filter
    if (filterVrm !== 'All') {
      if (hire.vehicles?.vrm !== filterVrm) return false;
    }

    // 2. Date Range Overlap
    if (filterStartDate || filterEndDate) {
      const filterStart = filterStartDate ? new Date(filterStartDate).getTime() : -8640000000000000;
      const filterEnd = filterEndDate ? new Date(filterEndDate).getTime() : 8640000000000000;

      const hireStart = new Date(hire.start_date).getTime();
      const hireEnd = hire.end_date ? new Date(hire.end_date).getTime() : 8640000000000000;

      const overlaps = (hireStart <= filterEnd) && (hireEnd >= filterStart);
      if (!overlaps) return false;
    }

    return true;
  });

  const filteredVehicles = vehicles.filter((vehicle) => {
    const q = vehicleSearchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (vehicle.vrm || '').toLowerCase().includes(q) ||
      (vehicle.make || '').toLowerCase().includes(q) ||
      (vehicle.model || '').toLowerCase().includes(q)
    );
  });

  const resolveVehicleId = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return '';
    const match = vehicles.find((v) => (v.vrm || '').toLowerCase() === trimmed);
    return match?.id ?? '';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Ongoing';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Hires</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage driver assignments and vehicle hire periods.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAuditClick}
            className={`px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-medium flex items-center gap-2 ${!hasAudit && 'opacity-75'}`}
            title="Preview agreement compliance before uploading"
          >
            {hasAudit ? <FileCheck size={16} className="text-primary-600 dark:text-primary-400" /> : <Lock size={16} />}
            Preview Agreement
          </button>
          <button
            onClick={() => setIsExtractModalOpen(true)}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-primary-600 text-white rounded-lg hover:from-blue-700 hover:to-primary-700 transition-all text-sm font-medium flex items-center gap-2 shadow-sm"
            title="Upload hire agreement and extract data automatically"
          >
            <FileSearch size={16} />
            Extract from Agreement
          </button>
          <button
            onClick={handleOpenModal}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Hire
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Error</h3>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col md:flex-row gap-4 items-end md:items-center">
        <div className="w-full md:w-64">
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Filter by Vehicle</label>
          <div className="relative">
            <Car className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <select
              value={filterVrm}
              onChange={(e) => setFilterVrm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            >
              <option value="All">All Vehicles</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.vrm}>{v.vrm}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex-1 grid grid-cols-2 gap-4 w-full">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">From Date</label>
            <input 
              type="date" 
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">To Date</label>
            <input 
              type="date" 
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
        </div>

        {(filterVrm !== 'All' || filterStartDate || filterEndDate) && (
          <button 
            onClick={() => {
              setFilterVrm('All');
              setFilterStartDate('');
              setFilterEndDate('');
            }}
            className="px-3 py-2 text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Ref</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Vehicle</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Driver</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Type</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Start</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Scheduled End</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Actual Return</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300 text-center">Doc</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredHires.length > 0 ? (
                  filteredHires.map((hire) => {
                    const isLateReturn = hire.actual_return_date && hire.end_date &&
                      new Date(hire.actual_return_date) > new Date(hire.end_date);

                    const getAssignmentTypeLabel = (type: string | null | undefined) => {
                      if (!type) return '-';
                      switch (type) {
                        case 'hire_lease': return 'Hire/Lease';
                        case 'pool_vehicle': return 'Pool Vehicle';
                        case 'allocated_driver': return 'Allocated';
                        default: return type;
                      }
                    };

                    const getAssignmentTypeColor = (type: string | null | undefined) => {
                      if (!type) return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
                      switch (type) {
                        case 'hire_lease': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                        case 'pool_vehicle': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
                        case 'allocated_driver': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                        default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
                      }
                    };

                    return (
                      <tr key={hire.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400 font-mono text-xs">
                          {hire.hire_ref || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-slate-800 dark:text-white font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                            {hire.vehicles?.vrm || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                          {hire.driver_name}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getAssignmentTypeColor(hire.assignment_type)}`}>
                            {getAssignmentTypeLabel(hire.assignment_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400 text-sm">
                          {formatDate(hire.start_date)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {hire.end_date ? (
                            <span className="text-slate-600 dark:text-slate-400">{formatDate(hire.end_date)}</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                              Ongoing
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {hire.actual_return_date ? (
                            <div className="flex flex-col">
                              <span className={isLateReturn ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-600 dark:text-slate-400'}>
                                {formatDate(hire.actual_return_date)}
                              </span>
                              {isLateReturn && (
                                <span className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1 mt-0.5">
                                  <AlertCircle size={12} />
                                  Late Return
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500 text-xs">Not returned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {hire.agreement_url ? (
                            <button
                              type="button"
                              onClick={() => void openAgreement(hire.agreement_url)}
                              className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                              title="View Agreement"
                            >
                              <FileText size={16} />
                            </button>
                          ) : (
                            <span className="text-slate-300 dark:text-slate-600" title="No Agreement">
                              -
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(hire)}
                            className="inline-flex items-center justify-center p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800 rounded-md transition-colors"
                            aria-label={`Edit hire for ${hire.driver_name}`}
                            title="Edit hire"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                      <div className="flex flex-col items-center justify-center">
                        <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="font-medium text-slate-900 dark:text-white">No hires found</p>
                        <p className="text-sm mt-1">
                          {hires.length === 0
                            ? "Add your first hire to get started."
                            : "Try adjusting your filters."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingHire ? "Edit Hire" : "Add New Hire"}
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {editingHire?.agreement_url && !agreementFile && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Current agreement uploaded.{' '}
                <button
                  type="button"
                  onClick={() => void openAgreement(editingHire.agreement_url)}
                  className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
                >
                  View
                </button>
                {' '}or upload a replacement below.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Vehicle <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                list="hire-vehicle-options"
                value={vehicleSearchTerm}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setVehicleSearchTerm(nextValue);
                  const resolvedId = resolveVehicleId(nextValue);
                  setFormData({ ...formData, vehicle_id: resolvedId });
                }}
                placeholder="Type to search vehicles..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                required
              />
              <datalist id="hire-vehicle-options">
                {filteredVehicles.map((v) => (
                  <option key={v.id} value={v.vrm}>
                    {[v.make, v.model].filter(Boolean).join(' ')}
                  </option>
                ))}
              </datalist>
            </div>
            {vehicles.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No vehicles found. Please add a vehicle first.</p>
            )}
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">
                {vehicles.length === 0
                  ? 'No vehicles available yet.'
                  : `${filteredVehicles.length} of ${vehicles.length} vehicles shown`}
              </span>
              <a
                href="/#/vehicles?new=1"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
              >
                Create vehicle
              </a>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Hire Reference
            </label>
            <input
              type="text"
              value={formData.hire_ref}
              onChange={(e) => setFormData({ ...formData,hire_ref: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
              placeholder="e.g. HIRE-2024-001"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Driver Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.driver_name}
              onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
              placeholder="John Doe"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Driver Email</label>
              <input
                type="email"
                value={formData.driver_email}
                onChange={(e) => setFormData({ ...formData, driver_email: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Driver Phone</label>
              <input
                type="tel"
                value={formData.driver_phone}
                onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="07700 900000"
              />
            </div>
          </div>

          {/* Assignment Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Assignment Type <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.assignment_type}
              onChange={(e) => setFormData({ ...formData, assignment_type: e.target.value as AssignmentType | '' })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="">Select assignment type...</option>
              <option value="hire_lease">Hire / Lease</option>
              <option value="pool_vehicle">Pool Vehicle</option>
              <option value="allocated_driver">Allocated Driver</option>
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Different types have different appeal logic and evidence requirements
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Start Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Scheduled End Date & Time
              </label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Expected return date per agreement
              </p>
            </div>
          </div>

          {/* Actual Return Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Actual Return Date & Time
            </label>
            <input
              type="datetime-local"
              value={formData.actual_return_date}
              onChange={(e) => setFormData({ ...formData, actual_return_date: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              When vehicle was actually returned. Overrides scheduled end for liability logic. Critical for late return detection.
            </p>
          </div>

          {/* Agreement Upload Section */}
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Hire Agreement
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Upload the signed agreement now to auto-fill future liability transfers.
            </p>
            
            <div 
              className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-colors cursor-pointer ${
                agreementFile 
                  ? 'border-green-300 bg-green-50 dark:bg-green-900/10 dark:border-green-800' 
                  : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept=".pdf,.jpg,.png" 
                onChange={handleFileChange}
              />
              {agreementFile ? (
                <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
                  <FileCheck className="w-8 h-8" />
                  <div className="text-left">
                    <p className="font-bold text-sm">{agreementFile.name}</p>
                    <p className="text-xs opacity-75">Click to change</p>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setAgreementFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="p-1 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-full"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="text-center text-slate-400 dark:text-slate-500">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm font-medium">Click to upload PDF or Image</p>
                </div>
              )}
            </div>
            {agreementFile && (
              <div className={`mt-2 text-xs ${formData.agreement_has_liability_clause ? 'text-green-700 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}>
                {formData.agreement_has_liability_clause
                  ? 'Liability clause verified by extraction.'
                  : 'Liability clause not yet verified by extraction.'}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {editingHire ? 'Update Hire' : 'Save Hire'}
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      <HireComplianceModal
        isOpen={isComplianceModalOpen}
        onClose={() => setIsComplianceModalOpen(false)}
      />

      <HireAgreementExtractModal
        isOpen={isExtractModalOpen}
        onClose={() => setIsExtractModalOpen(false)}
        onExtracted={handleExtractedData}
      />
    </div>
  );
};

export default Hires;
